import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  PersonaCard, 
  PersonaEditorModal, 
  PersonaShareModal,
  PersonaViewModal 
} from "@/components/personas";
import { 
  Plus, 
  Search, 
  Sparkles, 
  User, 
  Users, 
  Building2,
  Library
} from "lucide-react";
import { 
  fetchPersonas, 
  createPersona, 
  updatePersona,
  deletePersona,
  duplicatePersona,
  sharePersona,
  getPersonaAcl,
  fetchOrgUsers,
} from "@/lib/persona-api";
import type { 
  PersonaWithPermissions, 
  PersonaScope, 
  PersonaFormData,
  PersonaAcl,
  OrgUser,
  PersonaVisibility,
  PersonaPermission,
} from "@/lib/persona-types";
import { useToast } from "@/hooks/use-toast";

export default function PersonaLibrary() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<PersonaScope>("recommended");
  const [personas, setPersonas] = useState<PersonaWithPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [editorOpen, setEditorOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<PersonaWithPermissions | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Share modal data
  const [currentAcl, setCurrentAcl] = useState<PersonaAcl[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);

  const loadPersonas = async (scope: PersonaScope) => {
    setIsLoading(true);
    try {
      const data = await fetchPersonas(scope);
      setPersonas(data);
    } catch (error) {
      console.error("Failed to load personas:", error);
      toast({
        title: "Error",
        description: "Failed to load personas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPersonas(activeTab);
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as PersonaScope);
    setSearchQuery("");
  };

  const handleView = (persona: PersonaWithPermissions) => {
    setSelectedPersona(persona);
    setViewModalOpen(true);
  };

  const handleEdit = (persona: PersonaWithPermissions) => {
    setSelectedPersona(persona);
    setEditorOpen(true);
  };

  const handleShare = async (persona: PersonaWithPermissions) => {
    setSelectedPersona(persona);
    try {
      const [acl, users] = await Promise.all([
        getPersonaAcl(persona.id),
        fetchOrgUsers(),
      ]);
      setCurrentAcl(acl);
      setOrgUsers(users);
      setShareModalOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load sharing settings",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (persona: PersonaWithPermissions) => {
    setSelectedPersona(persona);
    setDeleteDialogOpen(true);
  };

  const handleDuplicate = async (persona: PersonaWithPermissions) => {
    try {
      await duplicatePersona(persona.id);
      toast({
        title: "Success",
        description: `Duplicated "${persona.name}" to your personas`,
      });
      if (activeTab === "mine") {
        loadPersonas("mine");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate persona",
        variant: "destructive",
      });
    }
  };

  const handleCreateNew = () => {
    setSelectedPersona(null);
    setEditorOpen(true);
  };

  const handleSavePersona = async (data: PersonaFormData) => {
    setIsSaving(true);
    try {
      if (selectedPersona) {
        await updatePersona(selectedPersona.id, data);
        toast({
          title: "Success",
          description: "Persona updated successfully",
        });
      } else {
        await createPersona(data);
        toast({
          title: "Success",
          description: "Persona created successfully",
        });
      }
      setEditorOpen(false);
      loadPersonas(activeTab);
    } catch (error) {
      toast({
        title: "Error",
        description: selectedPersona ? "Failed to update persona" : "Failed to create persona",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPersona) return;
    try {
      await deletePersona(selectedPersona.id);
      toast({
        title: "Success",
        description: "Persona deleted successfully",
      });
      setDeleteDialogOpen(false);
      loadPersonas(activeTab);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete persona",
        variant: "destructive",
      });
    }
  };

  const handleSaveShare = async (
    visibility: PersonaVisibility, 
    userPermissions: Array<{ user_id: string; permission: PersonaPermission }>
  ) => {
    if (!selectedPersona) return;
    setIsSaving(true);
    try {
      await sharePersona(selectedPersona.id, { visibility, user_permissions: userPermissions });
      toast({
        title: "Success",
        description: "Sharing settings updated",
      });
      loadPersonas(activeTab);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update sharing settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPersonas = personas.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <Library className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-bold">Persona Library</h1>
          </div>
          <p className="text-muted-foreground">
            Manage and reuse personas across your requirement reviews
          </p>
        </div>
        <Button onClick={handleCreateNew} className="gradient-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-2" />
          Create Persona
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="recommended" className="gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Recommended</span>
            </TabsTrigger>
            <TabsTrigger value="mine" className="gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">My Personas</span>
            </TabsTrigger>
            <TabsTrigger value="shared" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Shared with Me</span>
            </TabsTrigger>
            <TabsTrigger value="org" className="gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Org Library</span>
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search personas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Content */}
        <TabsContent value={activeTab} className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : filteredPersonas.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted mx-auto mb-6 flex items-center justify-center">
                <User className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No personas found</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {activeTab === "mine" 
                  ? "Create your first persona to get started. Personas help provide context for your requirement reviews."
                  : activeTab === "recommended"
                    ? "No template personas available yet. Check back later!"
                    : searchQuery 
                      ? "Try a different search term"
                      : "No personas available in this category yet."
                }
              </p>
              {activeTab === "mine" && (
                <Button onClick={handleCreateNew} className="gradient-primary text-primary-foreground">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Persona
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredPersonas.map(persona => (
                <PersonaCard
                  key={persona.id}
                  persona={persona}
                  onView={() => handleView(persona)}
                  onDuplicate={() => handleDuplicate(persona)}
                  onEdit={persona.can_edit ? () => handleEdit(persona) : undefined}
                  onShare={persona.can_share ? () => handleShare(persona) : undefined}
                  onDelete={persona.can_delete ? () => handleDelete(persona) : undefined}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <PersonaEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        persona={selectedPersona}
        onSave={handleSavePersona}
        isLoading={isSaving}
      />

      <PersonaViewModal
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        persona={selectedPersona}
      />

      <PersonaShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        persona={selectedPersona}
        currentAcl={currentAcl}
        orgUsers={orgUsers}
        onSave={handleSaveShare}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Persona</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPersona?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
